'use client';

import { PRIMARY_AUTH_ISSUER, useOAuthProvider } from '@modules/auth/providers/oauth-provider/oauth-provider';
import { useFlagsProxy } from '@modules/feature-flags/hooks/useFlagsProxy';
import { markAuthRedirectStarted } from '@modules/shared/analytics/scripts/fullstoryUtils';
import { ProfileOnboarding } from '@modules/shared/api/v2/api';

import { useRouter } from 'next/navigation';
import { SigninRedirectArgs } from 'oidc-client-ts';
import { createContext, useCallback, useContext, FC } from 'react';
import { useAuth } from 'react-oidc-context';
import { hasIdpManagedPermissions, hasIdpManagedProfile, parseOemConnection } from 'shared/src/user/types';
import { IdentityProvider } from 'shared/src/util';

export const ONBOARDING_CLAIM_NAME = 'https://cribl.cloud/onboarding';

/**
 * sessionStorage key that stores the intended post-login destination before an
 * external IdP redirect. Used to recover invite/deep-link return targets when
 * the OIDC callback state is missing or stale (e.g. incognito, double callback).
 */
export const AUTH_REDIRECT_FALLBACK_RETURN_TO_KEY = 'AUTH_REDIRECT_FALLBACK_RETURN_TO';

const isPersistableReturnTo = (returnTo: string): boolean => returnTo !== '/' && returnTo !== '/callback';

export const persistAuthRedirectReturnTo = (returnTo?: string): void => {
  if (returnTo && isPersistableReturnTo(returnTo)) {
    sessionStorage.setItem(AUTH_REDIRECT_FALLBACK_RETURN_TO_KEY, returnTo);
  }
};

export const consumeAuthRedirectReturnTo = (): string | null => {
  const stored = sessionStorage.getItem(AUTH_REDIRECT_FALLBACK_RETURN_TO_KEY);

  if (!stored) {
    return null;
  }

  sessionStorage.removeItem(AUTH_REDIRECT_FALLBACK_RETURN_TO_KEY);

  return stored;
};

export const clearAuthRedirectReturnTo = (): void => {
  sessionStorage.removeItem(AUTH_REDIRECT_FALLBACK_RETURN_TO_KEY);
};

export type AuthUserProfile = {
  sub?: string;
  email?: string;
  email_verified?: boolean;
  given_name?: string;
  family_name?: string;
  preferred_username?: string;
  [ONBOARDING_CLAIM_NAME]?: ProfileOnboarding;
  [key: string]: unknown;
};

export type PromptOptions = 'none' | 'login' | 'consent' | 'select_account' | undefined;
export interface LoginWithRedirectOptions {
  organizationId?: string;
  returnTo?: string;
  prompt?: PromptOptions;
  redirect_uri?: string;
  connection?: string;
  utm?: Record<string, string>;
  screen_hint?: string;
  loginHint?: string;
  // arbitrary query parameters to be added to the login redirect URL
  customQueryParams?: Record<string, string>;
}

export type CommonAuthProviderContextValue = {
  isLoading: boolean;
  isAuthenticated: boolean;
  hasIdpManagedProfile: boolean;
  hasIdpManagedPermissions: boolean;
  oemConnection: string | null;
  user?: AuthUserProfile;
  /**
   * Internal method for logging in. It will redirect user to a 3rd party SSO.
   * Normally you should use the `login` method instead, unless you are sure
   * you need to explicitly redirect to a 3rd party SSO.
   */
  loginWithRedirect: (params?: LoginWithRedirectOptions) => Promise<void>;
  logout: (params: { returnTo: string }) => Promise<void>;
  getAccessToken: (params?: { cacheMode?: 'off' }) => Promise<string | undefined>;
  getIdTokenClaims: () => Promise<AuthUserProfile | undefined>;
};

export type CommonAuthProviderContext = CommonAuthProviderContextValue & {
  /**
   * Your go-to method for logging users in. It will check the new login experience flag
   * and then redirect to the appropriate login experience in SSO or redirect
   * user to the new login experience pages in Maestro UI for the Discovery phase.
   */
  login: (params?: LoginWithRedirectOptions) => Promise<void>;
};

const authProvider = process.env.AUTH_CLIENT_PROVIDER;
const authProviderLabel = authProvider === IdentityProvider.AUTH0 ? ('auth0' as const) : ('okta' as const);

const CommonAuthProviderContext = createContext<CommonAuthProviderContextValue>({
  isLoading: false,
  isAuthenticated: false,
  hasIdpManagedProfile: false,
  hasIdpManagedPermissions: false,
  oemConnection: null,
  user: undefined,
  logout: () => {
    throw new Error('Context not initialized');
  },
  loginWithRedirect: () => {
    throw new Error('Context not initialized');
  },
  getAccessToken: () => {
    throw new Error('Context not initialized');
  },
  getIdTokenClaims: () => {
    throw new Error('Context not initialized');
  },
});

function genericAuthExtraQueryParams(params?: LoginWithRedirectOptions) {
  const result: Record<string, string> = {
    audience: process.env.AUTH_CLIENT_AUDIENCE_BROWSER ?? '',
    ...(params?.organizationId && { organization: params.organizationId }),
    ...(params?.prompt && { prompt: params.prompt }),
    ...params?.utm,
    ...(params?.screen_hint && { screen_hint: params.screen_hint }),
    ...(params?.loginHint && { login_hint: params.loginHint }),
    ...params?.customQueryParams,
  };

  if (params?.connection) {
    if (authProvider === IdentityProvider.OKTA) {
      result.idp = params.connection;
    } else {
      result.connection = params.connection;
    }
  }

  return result;
}

export const CommonAuthProvider: FC<React.PropsWithChildren> = ({ children }) => {
  const genericAuth = useAuth();
  const { getManager } = useOAuthProvider();

  const activeUser = genericAuth.user ?? null;

  // The preferred_username field in Auth0 is set to an email address and we need to check against the SSO prefix
  // on the sub.
  const idpIdentity =
    authProvider === IdentityProvider.OKTA
      ? (activeUser?.profile?.preferred_username ?? activeUser?.profile?.sub)
      : activeUser?.profile?.sub;

  const value: CommonAuthProviderContextValue = {
    isLoading: genericAuth.isLoading,
    user: activeUser?.profile as AuthUserProfile | undefined,
    isAuthenticated: genericAuth.isAuthenticated,
    // TODO - The preferred_username field in Auth0 is set to an email address. We either need to change that to be
    // consistent with Okta or come up with some new consistently-set profile field before we can use this provider
    // for Auth0
    hasIdpManagedPermissions: hasIdpManagedPermissions(idpIdentity),
    hasIdpManagedProfile: hasIdpManagedProfile(idpIdentity),
    oemConnection: parseOemConnection(genericAuth.user?.profile?.sub),
    logout: async (params) => genericAuth.signoutRedirect({ state: { returnTo: params.returnTo } }),
    getIdTokenClaims: async () => genericAuth.user?.profile,
    getAccessToken: async (params) => {
      if (params?.cacheMode === 'off') {
        return (await genericAuth.signinSilent({ prompt: 'none' }))?.access_token;
      }

      return activeUser?.access_token;
    },
    loginWithRedirect: async (params) => {
      markAuthRedirectStarted(authProviderLabel, window.location.pathname);
      persistAuthRedirectReturnTo(params?.returnTo);

      const manager = getManager();

      if (!manager) {
        throw new Error('UserManager not initialized');
      }

      const config: SigninRedirectArgs = {
        state: { returnTo: params?.returnTo },
        // Pass redirect_uri as a first-class arg (not via extraQueryParams) so oidc-client-ts
        // overrides the UserManager default instead of appending a duplicate redirect_uri,
        // which Auth0 rejects with "The redirect_uri was provided more than once".
        ...(params?.redirect_uri && { redirect_uri: params.redirect_uri }),
        extraQueryParams:
          manager.settings.authority !== PRIMARY_AUTH_ISSUER
            ? {
                ...(params?.loginHint && { login_hint: params.loginHint }),
                ...(params?.prompt && { prompt: params.prompt }),
                ...params?.customQueryParams,
              }
            : genericAuthExtraQueryParams(params),
      };

      return manager.signinRedirect(config);
    },
  };

  return <CommonAuthProviderContext.Provider value={value}>{children}</CommonAuthProviderContext.Provider>;
};

const forwardedParams = ['returnTo', 'loginHint'] as const satisfies (keyof LoginWithRedirectOptions)[];

export const useCommonAuthProvider = (): CommonAuthProviderContext => {
  const baseContext = useContext(CommonAuthProviderContext);
  const router = useRouter();
  const { cloudAuthNewLoginExperience } = useFlagsProxy();

  const login = useCallback(
    async (params?: LoginWithRedirectOptions) => {
      if (cloudAuthNewLoginExperience) {
        if (
          params?.connection ||
          params?.screen_hint === 'signup' ||
          params?.organizationId ||
          params?.prompt === 'none'
        ) {
          // If we already know the intended connection method, org-scoped login, silent re-auth, and/or signup
          // bypass the new login experience
          return baseContext.loginWithRedirect(params);
        }
        const query = new URLSearchParams();

        for (const param of forwardedParams) {
          if (params?.[param]) {
            query.append(param, params?.[param]);
          }
        }
        router.push('/login/identifier' + (query.size > 0 ? `?${query.toString()}` : ''));

        return;
      }

      return baseContext.loginWithRedirect(params);
    },
    [router, baseContext, cloudAuthNewLoginExperience],
  );

  return {
    ...baseContext,
    login,
  };
};

// https://nextjs.org/docs/app/api-reference/file-conventions/route-segment-config#dynamic
/** @public Ignore knip for NextJS-related dynamic import */
export const dynamic = 'force-dynamic';
